import { useState } from 'react';
import { Separator } from 'react-aria-components';
import { Trans, useLingui } from '@lingui/react/macro';

import { ClientFormDrawer } from '@/components/ClientForm/ClientFormDrawer';
import { ClientPaymentSection } from '@/components/ClientPaymentSection/ClientPaymentSection';
import ContactDetails from '@/components/ContactDetails';
import { RequestDueDate } from '@/components/DueDate';
import { EstimatedDueDate } from '@/components/EstimatedDueDate/EstimatedDueDate';
import { StoreResellerBadge } from '@/components/StoreResellerBadge';
import { UserAvatarGroup } from '@/components/UserAvatarGroup/UserAvatarGroup';
import config from '@/config';
import Accordion from '@/design_system/Accordion';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconCalendar from '@/icons/Calendar.svg';
import IconEdit from '@/icons/Edit.svg';
import IconFile from '@/icons/File.svg';
import IconStore from '@/icons/Store.svg';
import IconPerson from '@/icons/User.svg';
import {
  getArchivedAtDate,
  getEstimatedDueAtDate,
  isArchived,
  RequestWithRelations,
  useAfterSalesManager,
  useRequestDocuments,
} from '@/models/request';
import { useStore } from '@/models/store';
import { shouldShowClientPaymentSection, useClientPaymentTransactions } from '@/models/transaction';
import { hasPermission, hasPermissionOnAnyScope } from '@/models/user';
import { SupervisorPicker } from '@/routes/Requests/components/SupervisorPicker';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useCurrentSession } from '@/services/auth';
import { useDatabaseTranslation } from '@/services/i18n';
import { formatDate } from '@/utils/date';

export const RequestInfoSidePanel = () => {
  const { t } = useLingui();
  const { currentSession } = useCurrentSession();
  const { request, workflow, view, iam } = useRequestContext();
  const [isOpenClientFormDrawer, setIsOpenClientFormDrawer] = useState(false);
  const canEditClient =
    !isArchived(request) &&
    hasPermission(currentSession, 'edit_client', {
      organizationId: request.organizationId,
    });

  const { data: transactions = [] } = useClientPaymentTransactions(request.id);

  const { data: { user: afterSalesManager } = {} } = useAfterSalesManager(
    {
      requestId: request.id,
    },
    {
      enabled: iam.hasOnlyExternalWorkshopScopeOnRequest,
    }
  );

  const { data: store } = useStore(request?.storeId);

  const { data: generatedDocuments = [] } = useRequestDocuments({
    requestId: request.id,
  });

  const dropOffReceipt = workflow?.config.dropOffReceipt?.enabled;
  const pickUpReceipt = generatedDocuments.find((doc) => doc.type === 'pick-up-receipt');

  const showClientPaymentSection = shouldShowClientPaymentSection({
    canSeeClientPrice: view.organizationPrice.shown,
    transactionsCount: transactions.length,
    request,
  });
  const canViewTransactions = hasPermissionOnAnyScope(
    currentSession,
    'view_invoice',
    request.organizationId
  );

  return (
    <Stack gap="0.5rem">
      <Stack gap="0.5rem" ariaLabel={t({ id: 'request.information', message: 'Information' })}>
        <Accordion
          openByDefault
          trigger={
            <Paragraph weight="medium">
              <Trans id="request.information">Information</Trans>
            </Paragraph>
          }
        >
          <RequestDetails request={request} />
        </Accordion>
      </Stack>
      {!!showClientPaymentSection && (
        <>
          <Separator />
          <ClientPaymentSection
            request={request}
            transactions={transactions}
            canViewTransactions={canViewTransactions}
          />
        </>
      )}

      {request.client && view.client.enabled && (
        <>
          <Separator />
          <Stack ariaLabel={t({ id: 'request.contact-details.client', message: 'Client' })}>
            <Accordion
              openByDefault
              trigger={
                <Paragraph weight="medium">
                  <Trans id="request.contact-details.client">Client</Trans>
                </Paragraph>
              }
            >
              <Stack gap="1rem">
                <ContactDetails
                  type="client"
                  name={request.client.name}
                  email={request.client.email}
                  address={request.client.address}
                  phone={request.client.phone}
                  deliveryChoice={request.client.deliveryOption}
                  isVIP={request.vip}
                  documents={
                    request.client && request.store
                      ? [
                          dropOffReceipt
                            ? {
                                label: view.client.dropoffSignature.enabled
                                  ? t({
                                      id: 'brand.request-info.client-info.signed-document',
                                      message: 'Signed drop-off receipt',
                                    })
                                  : t({
                                      id: 'brand.request-info.client-info.document',
                                      message: 'Drop-off receipt',
                                    }),
                                url: config.apiUrl + `/requests/${request.id}/drop-off-receipt`,
                              }
                            : undefined,
                          pickUpReceipt
                            ? {
                                label: view.client.pickupSignature.enabled
                                  ? t({
                                      id: 'brand.request-info.client-info.signed-pick-up',
                                      message: 'Signed pickup receipt',
                                    })
                                  : t({
                                      id: 'brand.request-info.client-info.pick-up',
                                      message: 'Pickup receipt',
                                    }),
                                url: pickUpReceipt.url,
                              }
                            : undefined,
                        ].filter((doc) => !!doc)
                      : undefined
                  }
                  size="small"
                  placeIcon={null}
                />
                {canEditClient && (
                  <Button
                    variant="secondary"
                    onPress={() => setIsOpenClientFormDrawer(true)}
                    size="small"
                    ariaLabel={t({ id: 'request.edit.actions.edit-client', message: 'Edit' })}
                  >
                    <IconEdit />
                    <Trans id="request.edit.actions.edit-client">Edit</Trans>
                  </Button>
                )}
              </Stack>
            </Accordion>
          </Stack>
          <ClientFormDrawer
            isOpen={isOpenClientFormDrawer}
            setIsOpen={setIsOpenClientFormDrawer}
            hideDeliveryOption={!request.storeId}
            variant="default"
            size="medium"
          />
        </>
      )}
      {request.store && (
        <>
          <Separator />
          <Accordion
            openByDefault
            trigger={
              <Stack row gap="0.25rem" alignItems="center">
                <Paragraph weight="medium">
                  <Trans id="request.contact-details.store">Store</Trans>
                </Paragraph>
                {!!store?.organizationConfig?.external && <StoreResellerBadge />}
              </Stack>
            }
          >
            <Stack ariaLabel={t({ id: 'request.contact-details.store', message: 'Store' })}>
              <ContactDetails
                type="store"
                name={request.store.name}
                email={request.store.address?.contactEmail}
                address={request.store.address}
                phone={request.store.contactInfo?.phone}
                openingHours={request.store.storeConfig?.openingHours}
                size="small"
                placeIcon={null}
              />
            </Stack>
          </Accordion>
        </>
      )}
      {afterSalesManager && (
        <>
          <Separator />
          <Accordion
            openByDefault
            trigger={
              <Paragraph weight="medium">
                <Trans id="request.contact-details.store.asm">Contact</Trans>
              </Paragraph>
            }
          >
            <Stack ariaLabel={t({ id: 'request.contact-details.store.asm', message: 'Contact' })}>
              <ContactDetails
                type="client"
                name={afterSalesManager.name}
                email={afterSalesManager.email}
                phone={afterSalesManager.phone}
                size="small"
              />
            </Stack>
          </Accordion>
        </>
      )}
    </Stack>
  );
};

export const RequestInfoTopPanel = () => {
  const { currentSession } = useCurrentSession();
  const { t } = useLingui();
  const { request, workflow, view } = useRequestContext();
  const [isOpenClientFormDrawer, setIsOpenClientFormDrawer] = useState(false);
  const canEditClient =
    !isArchived(request) &&
    hasPermission(currentSession, 'edit_client', {
      organizationId: request.organizationId,
    });
  const { data: transactions = [] } = useClientPaymentTransactions(request.id);

  const { data: { user: afterSalesManager } = {} } = useAfterSalesManager({
    requestId: request.id,
  });

  const { data: generatedDocuments = [] } = useRequestDocuments({
    requestId: request.id,
  });

  const dropOffReceipt = workflow?.config.dropOffReceipt?.enabled;
  const pickUpReceipt = generatedDocuments.find((doc) => doc.type === 'pick-up-receipt');

  const showClientPaymentSection = shouldShowClientPaymentSection({
    canSeeClientPrice: view.organizationPrice.shown,
    transactionsCount: transactions.length,
    request,
  });
  const canViewTransactions = hasPermissionOnAnyScope(
    currentSession,
    'view_invoice',
    request.organizationId
  );

  return (
    <Box padding="0">
      <Stack ariaLabel={t({ id: 'request.information.title', message: 'Information' })}>
        <Accordion
          trigger={
            <Stack row gap="0.5rem" alignItems="center">
              <IconFile />
              <Trans id="request.information">Information</Trans>
            </Stack>
          }
        >
          <RequestDetails request={request} />
        </Accordion>
      </Stack>

      {!!showClientPaymentSection && (
        <>
          <Separator />
          <ClientPaymentSection
            openByDefault={false}
            request={request}
            transactions={transactions}
            canViewTransactions={canViewTransactions}
          />
        </>
      )}

      {request.client && view.client.enabled && (
        <>
          <Separator />
          <Stack ariaLabel={t({ id: 'request.contact-details.client', message: 'Client' })}>
            <Accordion
              trigger={
                <Stack row gap="0.5rem" alignItems="center" flexWrap="nowrap">
                  <IconPerson />
                  {request.client.name || (
                    <Trans id="request.contact-details.client.label">Client</Trans>
                  )}
                </Stack>
              }
            >
              <Stack gap="1rem">
                <ContactDetails
                  type="client"
                  email={request.client.email}
                  address={request.client.address}
                  phone={request.client.phone}
                  deliveryChoice={request.client.deliveryOption}
                  documents={
                    request.client && request.store
                      ? [
                          dropOffReceipt
                            ? {
                                label: view.client.dropoffSignature.enabled
                                  ? t({
                                      id: 'brand.request-info.client-info.signed-document',
                                      message: 'Signed drop-off receipt',
                                    })
                                  : t({
                                      id: 'brand.request-info.client-info.document',
                                      message: 'Drop-off receipt',
                                    }),
                                url: config.apiUrl + `/requests/${request.id}/drop-off-receipt`,
                              }
                            : undefined,
                          pickUpReceipt
                            ? {
                                label: view.client.pickupSignature.enabled
                                  ? t({
                                      id: 'brand.request-info.client-info.signed-pick-up',
                                      message: 'Signed pickup receipt',
                                    })
                                  : t({
                                      id: 'brand.request-info.client-info.pick-up',
                                      message: 'Pickup receipt',
                                    }),
                                url: pickUpReceipt.url,
                              }
                            : undefined,
                        ].filter((doc) => !!doc)
                      : undefined
                  }
                  size="small"
                />
                {canEditClient && (
                  <Button
                    variant="secondary"
                    onPress={() => setIsOpenClientFormDrawer(true)}
                    size="small"
                    ariaLabel={t({ id: 'request.edit.actions.edit-client', message: 'Edit' })}
                  >
                    <IconEdit />
                    <Trans id="request.edit.actions.edit-client">Edit</Trans>
                  </Button>
                )}
              </Stack>
            </Accordion>
          </Stack>
          <ClientFormDrawer
            isOpen={isOpenClientFormDrawer}
            setIsOpen={setIsOpenClientFormDrawer}
            hideDeliveryOption={!request.storeId}
            variant="default"
          />
        </>
      )}
      {request.store && (
        <>
          <Separator />
          <Stack ariaLabel={t({ id: 'request.contact-details.store', message: 'Store' })}>
            <Accordion
              trigger={
                <Stack row gap="0.5rem" alignItems="center">
                  <IconStore />
                  {request.store.name}
                </Stack>
              }
            >
              <ContactDetails
                type="store"
                email={request.store.address?.contactEmail}
                address={request.store.address}
                phone={request.store.contactInfo?.phone}
                openingHours={request.store.storeConfig?.openingHours}
                size="small"
              />
            </Accordion>
          </Stack>
        </>
      )}
      {afterSalesManager && (
        <>
          <Separator />
          <Stack ariaLabel={t({ id: 'request.contact-details.store.asm', message: 'Contact' })}>
            <Accordion
              trigger={
                <Stack row gap="0.5rem" alignItems="center">
                  <IconPerson />
                  <Trans id="request.contact-details.store.asm">Contact</Trans>
                </Stack>
              }
            >
              <ContactDetails
                type="client"
                name={afterSalesManager.name}
                email={afterSalesManager.email}
                phone={afterSalesManager.phone}
                size="small"
              />
            </Accordion>
          </Stack>
        </>
      )}
    </Box>
  );
};

const RequestDetails = ({ request }: { request: RequestWithRelations }) => {
  const { view, iam } = useRequestContext();

  return (
    <Stack gap="0.75rem">
      <RequestRef request={request} />

      {view.information.externalReference.enabled && !!request.externalReference && (
        <RequestExternalReference request={request} />
      )}

      <CreationDate request={request} />

      {!iam.hasOnlyExternalWorkshopScopeOnRequest && (
        <>
          <RequestDueDate
            date={request.dueAt ? new Date(request.dueAt) : null}
            variant="info"
            estimatedDate={isArchived(request) ? null : getEstimatedDueAtDate(request)}
          />
          <EstimatedDueDate
            variant="info"
            estimatedDate={isArchived(request) ? null : getEstimatedDueAtDate(request)}
          />
        </>
      )}

      <RequestSalesAdvisor request={request} />

      <RequestCustomFields request={request} />

      {request.externalPaymentReference && <PaymentRef request={request} />}

      {isArchived(request) && !!getArchivedAtDate(request) && <ArchiveDate request={request} />}

      <SupervisorPicker request={request} variant="sidebar" />

      <Collaborators request={request} />
    </Stack>
  );
};

const RequestRef = ({ request }: { request: RequestWithRelations }) => {
  const { t } = useLingui();

  return (
    <Stack>
      <Paragraph color="secondary" size="200">
        <Trans id="request.reference.long">Reference</Trans>
      </Paragraph>
      <Paragraph
        weight="medium"
        aria-label={t({ id: 'request.reference.long', message: 'Reference' })}
      >
        {request.reference}
      </Paragraph>
    </Stack>
  );
};

const RequestExternalReference = ({ request }: { request: RequestWithRelations }) => {
  const { t } = useLingui();

  return (
    <Stack>
      <Paragraph color="secondary" size="200">
        <Trans id="request.external-reference">Internal reference</Trans>
      </Paragraph>
      <Paragraph
        weight="medium"
        aria-label={t({ id: 'request.external-reference', message: 'Internal reference' })}
      >
        {request.externalReference}
      </Paragraph>
    </Stack>
  );
};

const CreationDate = ({ request }: { request: RequestWithRelations }) => {
  return (
    <Stack>
      <Paragraph color="secondary" size="200">
        <Trans id="request.side-menu.request-details.creation-date">Creation date</Trans>
      </Paragraph>
      <Stack row alignItems="center" gap="0.25rem">
        <IconCalendar style={{ fontSize: '1rem' }} />
        <Paragraph weight="medium">
          {formatDate(new Date(request.referencedAt ?? request.createdAt), { dateStyle: 'medium' })}
        </Paragraph>
      </Stack>
    </Stack>
  );
};

const PaymentRef = ({ request }: { request: RequestWithRelations }) => {
  const { t } = useLingui();

  return (
    <Stack>
      <Paragraph color="secondary" size="200">
        <Trans id="request.payment-ref">Receipt / invoice number</Trans>
      </Paragraph>
      <Paragraph
        weight="medium"
        aria-label={t({ id: 'request.payment-ref', message: 'Receipt / invoice number' })}
      >
        {request.externalPaymentReference}
      </Paragraph>
    </Stack>
  );
};

const ArchiveDate = ({ request }: { request: RequestWithRelations }) => {
  const { t } = useLingui();

  const archivedAtDate = getArchivedAtDate(request);

  if (!archivedAtDate) {
    return null;
  }

  return (
    <Stack>
      <Paragraph color="secondary" size="200">
        <Trans id="request.archived-date">Archived date</Trans>
      </Paragraph>
      <Stack
        row
        alignItems="center"
        gap="0.25rem"
        aria-label={t({ id: 'request.archived-date', message: 'Archived date' })}
      >
        <IconCalendar style={{ fontSize: '1rem' }} />
        <Paragraph weight="medium">{formatDate(archivedAtDate, { dateStyle: 'medium' })}</Paragraph>
      </Stack>
    </Stack>
  );
};

const RequestSalesAdvisor = ({ request }: { request: RequestWithRelations }) => {
  const { t } = useLingui();
  const { view } = useRequestContext();

  if (!request.salesAdvisor || !view.information.salesAdvisor.enabled) {
    return null;
  }

  return (
    <Stack>
      <Paragraph color="secondary" size="200">
        <Trans id="request.sales-advisor">Sales advisor</Trans>
      </Paragraph>
      <Paragraph
        weight="medium"
        aria-label={t({ id: 'request.sales-advisor', message: 'Sales advisor' })}
      >
        {request.salesAdvisor}
      </Paragraph>
    </Stack>
  );
};

const RequestCustomFields = ({ request }: { request: RequestWithRelations }) => {
  const { t } = useLingui();
  const { view } = useRequestContext();
  const { _db } = useDatabaseTranslation();

  if (!request.customFieldValues?.length) {
    return null;
  }

  return (
    <>
      {request.customFieldValues.map((customFieldValue) => {
        const customField = view.information.customFields.find(
          (_customField) => _customField.id === customFieldValue.id
        );

        if (!customField) {
          return null;
        }

        return (
          <Stack key={customFieldValue.id}>
            <Paragraph color="secondary" size="200">
              {_db(customField.label)}
            </Paragraph>
            <Paragraph
              weight="medium"
              aria-label={t({ id: 'request.sales-advisor', message: 'Sales advisor' })}
            >
              {customFieldValue.value || '-'}
            </Paragraph>
          </Stack>
        );
      })}
    </>
  );
};

const Collaborators = ({ request }: { request: RequestWithRelations }) => {
  const { t } = useLingui();
  const label = t({ id: 'request.collaborators', message: 'Collaborators' });

  return (
    <Stack gap="0.125rem">
      <Paragraph color="secondary" size="200">
        {label}
      </Paragraph>
      <Stack row gap="0.25rem" alignItems="center">
        {request.collaborators?.length ? (
          <UserAvatarGroup users={request.collaborators} size="small" />
        ) : (
          <Paragraph weight="medium">- </Paragraph>
        )}
      </Stack>
    </Stack>
  );
};
